import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Star } from "lucide-react";

interface SupplierEvaluationDialogProps {
  open: boolean;
  onClose: () => void;
  evaluation: any;
}

export const SupplierEvaluationDialog = ({ open, onClose, evaluation }: SupplierEvaluationDialogProps) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    supplier_id: "",
    evaluation_date: new Date().toISOString().split('T')[0],
    evaluation_period: "",
    quality_score: 3,
    delivery_performance: 3,
    pricing_competitiveness: 3,
    compliance_certification: 3,
    communication_responsiveness: 3,
    technical_support: 3,
    strengths: "",
    weaknesses: "",
    improvement_actions: "",
    has_iso_certification: false,
    evaluation_status: "pending",
    recommendation: "approved",
    next_evaluation_date: "",
  });

  const { data: suppliers } = useQuery({
    queryKey: ['suppliers-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (evaluation) {
      setFormData({
        supplier_id: evaluation.supplier_id,
        evaluation_date: evaluation.evaluation_date,
        evaluation_period: evaluation.evaluation_period,
        quality_score: evaluation.quality_score,
        delivery_performance: evaluation.delivery_performance,
        pricing_competitiveness: evaluation.pricing_competitiveness,
        compliance_certification: evaluation.compliance_certification,
        communication_responsiveness: evaluation.communication_responsiveness,
        technical_support: evaluation.technical_support,
        strengths: evaluation.strengths || "",
        weaknesses: evaluation.weaknesses || "",
        improvement_actions: evaluation.improvement_actions || "",
        has_iso_certification: evaluation.has_iso_certification || false,
        evaluation_status: evaluation.evaluation_status || "pending",
        recommendation: evaluation.recommendation,
        next_evaluation_date: evaluation.next_evaluation_date || "",
      });
    }
  }, [evaluation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (evaluation) {
        const { error } = await supabase
          .from('supplier_evaluations')
          .update(formData)
          .eq('id', evaluation.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('supplier_evaluations')
          .insert([{ ...formData, evaluator_id: user.id }]);
        if (error) throw error;
      }

      toast.success(t('savedSuccessfully'));
      onClose();
    } catch (error) {
      console.error('Error saving evaluation:', error);
      toast.error(t('errorSavingData'));
    }
  };

  const ScoreSlider = ({ label, value, onChange }: any) => (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-sm">{label}</Label>
        <span className="text-sm font-medium text-orange-500">{value}/5</span>
      </div>
      <div className="relative">
        <Slider
          value={[value]}
          onValueChange={(v) => onChange(v[0])}
          min={1}
          max={5}
          step={1}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{t('poor')}</span>
          <span>{t('fair')}</span>
          <span>{t('average')}</span>
          <span>{t('good')}</span>
          <span>{t('excellent')}</span>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('supplierEvaluation')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('supplier')} *</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}
                disabled={!!evaluation}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectSupplier')} />
                </SelectTrigger>
                <SelectContent>
                  {suppliers?.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('evaluationDate')} *</Label>
              <Input
                type="date"
                value={formData.evaluation_date}
                onChange={(e) => setFormData({ ...formData, evaluation_date: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-4 border rounded-lg p-4 bg-muted/50">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-orange-500" />
              <h4 className="font-semibold">{t('criteria')} {t('supplierEvaluation')} (ISO 9001)</h4>
            </div>
            
            <ScoreSlider
              label={t('qualityScore')}
              value={formData.quality_score}
              onChange={(v: number) => setFormData({ ...formData, quality_score: v })}
            />
            <ScoreSlider
              label={t('deliveryPerformance')}
              value={formData.delivery_performance}
              onChange={(v: number) => setFormData({ ...formData, delivery_performance: v })}
            />
            <ScoreSlider
              label={t('pricingCompetitiveness')}
              value={formData.pricing_competitiveness}
              onChange={(v: number) => setFormData({ ...formData, pricing_competitiveness: v })}
            />
            <ScoreSlider
              label={t('complianceCertification')}
              value={formData.compliance_certification}
              onChange={(v: number) => setFormData({ ...formData, compliance_certification: v })}
            />
            <ScoreSlider
              label={t('communicationResponsiveness')}
              value={formData.communication_responsiveness}
              onChange={(v: number) => setFormData({ ...formData, communication_responsiveness: v })}
            />
            <ScoreSlider
              label={t('technicalSupport')}
              value={formData.technical_support}
              onChange={(v: number) => setFormData({ ...formData, technical_support: v })}
            />
          </div>

          <div className="space-y-4">
            <div>
              <Label>{t('strengths')}</Label>
              <Textarea
                value={formData.strengths}
                onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                rows={3}
                placeholder={t('recordSupplierStrengths')}
              />
            </div>
            <div>
              <Label>{t('weaknesses')}</Label>
              <Textarea
                value={formData.weaknesses}
                onChange={(e) => setFormData({ ...formData, weaknesses: e.target.value })}
                rows={3}
                placeholder={t('recordSupplierWeaknesses')}
              />
            </div>
            <div>
              <Label>{t('improvementActions')}</Label>
              <Textarea
                value={formData.improvement_actions}
                onChange={(e) => setFormData({ ...formData, improvement_actions: e.target.value })}
                rows={3}
                placeholder={t('suggestImprovements')}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2 border rounded-lg p-3">
            <Checkbox
              id="iso-cert"
              checked={formData.has_iso_certification}
              onCheckedChange={(checked) => 
                setFormData({ ...formData, has_iso_certification: checked as boolean })
              }
            />
            <label
              htmlFor="iso-cert"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t('supplierHasIsoCertification')}
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('evaluationStatus')} *</Label>
              <Select
                value={formData.evaluation_status}
                onValueChange={(v) => setFormData({ ...formData, evaluation_status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">{t('pending')}</SelectItem>
                  <SelectItem value="approved">{t('approved')}</SelectItem>
                  <SelectItem value="rejected">{t('rejected')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('nextEvaluationDate')}</Label>
              <Input
                type="date"
                value={formData.next_evaluation_date}
                onChange={(e) => setFormData({ ...formData, next_evaluation_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!!evaluation}>
              {t('saveEvaluation')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};